import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { FiCheck, FiX, FiImage, FiTrash2 } from 'react-icons/fi';
import '../../styles/infographics.css';

// Свой фон для шаблона инфографики.
// В приложении картинка обрезается под cut_size_width × cut_size_height
// прямо в пикере (CreateSocialPost.openGallery) и уходит на сервер как base64.
// Здесь то же самое: выбираем файл, кадрируем в тех же пропорциях,
// отдаём холст ровно нужного размера.
//
// Формат значения совпадает с приложением:
//   { url: 'data:image/jpeg;base64,...', data: '<base64 без префикса>',
//     time: '?time=...', isChanged: true }

const DEFAULT_WIDTH = 1080;
const DEFAULT_HEIGHT = 1350;
// Больше 4000 px по стороне сервер всё равно не ждёт, а base64 раздувается
const MAX_SIDE = 4000;

const renderToBase64 = (imageSrc, pixelCrop, targetWidth, targetHeight) => (
    new Promise((resolve, reject) => {
        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            let width = targetWidth;
            let height = targetHeight;
            if (width > MAX_SIDE || height > MAX_SIDE) {
                const scale = MAX_SIDE / Math.max(width, height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
                0, 0, width, height,
            );
            // 0.8 — то же качество, что в приложении (compressImageQuality)
            resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        image.onerror = reject;
    })
);

export default function BackgroundPicker({ label, value, onChange, pattern }) {
    const [source, setSource] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [area, setArea] = useState(null);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const targetWidth = Number(pattern && pattern.cut_size_width) > 0
        ? Number(pattern.cut_size_width) : DEFAULT_WIDTH;
    const targetHeight = Number(pattern && pattern.cut_size_height) > 0
        ? Number(pattern.cut_size_height) : DEFAULT_HEIGHT;

    const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

    const pickFile = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Нужен файл с картинкой.');
            return;
        }
        setError(null);
        const reader = new FileReader();
        reader.onload = () => {
            setSource(reader.result);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        };
        reader.readAsDataURL(file);
        // Сбрасываем input, иначе повторный выбор того же файла не сработает
        event.target.value = '';
    };

    const apply = async () => {
        if (!source || !area) return;
        try {
            const dataUrl = await renderToBase64(source, area, targetWidth, targetHeight);
            onChange({
                url: dataUrl,
                data: dataUrl.split(',')[1],
                time: '?time=' + new Date(),
                isChanged: true,
            });
            setSource(null);
        } catch (e) {
            console.error('Ошибка обрезки фона:', e);
            setError('Не удалось обработать картинку.');
        }
    };

    const clear = () => {
        onChange({ url: '', data: false, time: '?time=' + new Date(), isChanged: false });
    };

    const chosen = value && value.data;

    return (
        <div className="igControl">
            <div className="igControlHead">
                <span className="igControlLabel">{label}</span>
                <span className="igControlHint">
                    Обрежется под {targetWidth}×{targetHeight}
                </span>
            </div>

            {chosen ? (
                <div className="igBgChosen">
                    <img src={value.url} alt="Фон" />
                    <div className="igBgChosenActions">
                        <button type="button" className="igGhostBtn" onClick={() => inputRef.current.click()}>
                            <FiImage /> Заменить
                        </button>
                        <button type="button" className="igGhostBtn" onClick={clear}>
                            <FiTrash2 /> Убрать
                        </button>
                    </div>
                </div>
            ) : (
                <button type="button" className="igPicker" onClick={() => inputRef.current.click()}>
                    <span className="igPickerPlaceholder">Выбрать картинку</span>
                    <FiImage />
                </button>
            )}

            {error ? <p className="igControlError">{error}</p> : null}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={pickFile}
            />

            {source && (
                <div className="imageCropperOverlay" onClick={() => setSource(null)}>
                    <div className="imageCropperModal" onClick={(e) => e.stopPropagation()}>
                        <div className="imageCropperHeader">
                            <h3>Выберите область фона</h3>
                            <button className="closeButton" onClick={() => setSource(null)}>
                                <FiX />
                            </button>
                        </div>

                        <div className="cropperContainer">
                            <Cropper
                                image={source}
                                crop={crop}
                                zoom={zoom}
                                aspect={targetWidth / targetHeight}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>

                        <div className="cropperControls">
                            <div className="zoomControl">
                                <label>Масштаб</label>
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    onChange={(e) => setZoom(Number(e.target.value))}
                                />
                            </div>
                            <div className="cropperButtons">
                                <button className="cancelButton" onClick={() => setSource(null)}>
                                    <FiX /> Отмена
                                </button>
                                <button className="confirmButton" onClick={apply}>
                                    <FiCheck /> Применить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
